import Inherit from '../../core/inherit';
import ShelfController from '../../core/shelf.controller';
import StoreCheckDelegate from './storecheck.delegate';
import { calculateAuditDuration, fmtStoreCheck } from '../../utils/timeUtils';
import { productList } from '../common';
import { formatPriceName } from '../../utils/priceName';

// Prefer the linked barcode's "[segment] [name] - [grams]g" label; fall back to the captured name.
const MODERN_STORE_TYPES = ['nacional', 'regional', 'independiente'];

const storeCheckProductName = (prod) => {
    const b = prod?.linkedBarcode;
    return b?.name
        ? formatPriceName({ segment: b.segment, productName: b.name, grams: b.grams })
        : prod?.name || 'Unknown Product';
};

class StoreCheckController extends Inherit(ShelfController, StoreCheckDelegate) {
    shouldComponentUpdate(nextProps, nextState) {
        return (
            this.state.storeCheck !== nextState.storeCheck ||
            this.state.prices !== nextState.prices ||
            this.state.date !== nextState.date ||
            this.state.loading !== nextState.loading ||
            this.state.elementsLoading !== nextState.elementsLoading
        );
    }

    /** Channel of the audited store, derived from its store type (decides the price columns). */
    getChannel(store) {
        return MODERN_STORE_TYPES.includes(store?.storeType) ? 'moderno' : 'tradicional';
    }

    async initialize() {
        let storeCheck = null;
        let prices = null;
        let date = null;
        let store = null;

        try {
            if (this.isAuthenticated) {
                const storeCheckId = this.props.searchParams.get('id');

                if (!storeCheckId) {
                    this.setState({ loading: false, elementsLoading: false });
                    return;
                }

                try {
                    storeCheck = await this.data.fetchStoreCheckById(storeCheckId);
                } catch {
                    this.props.onMessage?.('error', 'Error al cargar la auditoría');
                    this.setState({ loading: false, elementsLoading: false });
                    return;
                }

                if (storeCheck) {
                    try {
                        const storeCheckData = storeCheck?.data;
                        prices = await this.fetchPriceList(storeCheckData, store);

                        if (storeCheckData.storeId) {
                            store = storeCheckData?.storeId;
                        }

                        date = fmtStoreCheck(storeCheckData?.createdAt, 'DD/MM/YYYY');
                    } catch {
                        this.props.onMessage?.('warning', 'Algunos datos de la auditoría no pudieron cargarse');
                    }
                }
            } else {
                this.navigateLogin();
                return;
            }
        } catch {
            this.props.onMessage?.('error', 'Error inesperado al cargar la auditoría');
        }

        const storeCheckData = storeCheck?.data;
        const normalizedStoreCheck = storeCheck
            ? {
                  ...storeCheckData,
                  user: storeCheckData?.userId,
                  categories: storeCheckData?.categories || [],
                  products: storeCheckData?.products || [],
                  duration: calculateAuditDuration(storeCheckData),
              }
            : null;

        this.setState({
            storeCheck: normalizedStoreCheck,
            prices: prices || [],
            date,
            store: store || {},
            loading: false,
            elementsLoading: false,
        });
    }

    async fetchPriceList(storeCheck, store) {
        try {
            if (!storeCheck?.products) {
                return [];
            }

            const filteredProds = storeCheck.products.filter((prod) => prod?.isAvailable) || [];

            if (filteredProds.length === 0) {
                return [];
            }

            // The store's own (per-piso) price list when it has one, else its client's / store type's.
            const storeId = store?._id || storeCheck?.store?._id || storeCheck?.store;

            let priceListProducts = [];
            if (storeId) {
                try {
                    const checkDate = storeCheck?.createdAt;
                    const priceResponse = await this.data.fetchPricesForStore(storeId, checkDate);
                    priceListProducts = priceResponse?.products || priceResponse?.data?.products || [];
                } catch {
                    // Suggested prices may be unavailable; continue with store prices only
                }
            }

            const suggestedByProductId = new Map(
                priceListProducts.map((p) => [p.productId, p.suggestedPrice ?? p.price ?? 0])
            );
            const costByProductId = new Map(priceListProducts.map((p) => [p.productId, p.price ?? 0]));

            const priceData = filteredProds.map((prod, index) => {
                try {
                    const productPrice = prod?.price || 0;
                    const productId = prod?.productId || productList?.find((p) => p.name === prod?.name)?.value;
                    const suggestedPrice = suggestedByProductId.has(productId)
                        ? suggestedByProductId.get(productId)
                        : prod?.suggestedPrice ?? null;
                    const cost = costByProductId.get(productId) ?? null;

                    const margin =
                        productPrice && suggestedPrice
                            ? ((productPrice - suggestedPrice) / productPrice) * 100
                            : cost && productPrice
                              ? ((productPrice - cost) / productPrice) * 100
                              : 0;

                    const suggestedMargin =
                        suggestedPrice && cost ? (((suggestedPrice - cost) / suggestedPrice) * 100).toFixed(2) : '0.0';

                    const priceVar =
                        suggestedPrice && productPrice ? (productPrice / suggestedPrice - 1) * 100 : 0;

                    return {
                        key: `product-${index}`,
                        name: storeCheckProductName(prod),
                        code: prod?.linkedBarcode?.code || null,
                        storePrice: isNaN(productPrice) ? '0.0' : productPrice.toFixed(2),
                        margin: `${isNaN(margin) ? '0.0' : margin.toFixed(2)}%`,
                        suggestedPrice:
                            suggestedPrice != null && !isNaN(suggestedPrice)
                                ? suggestedPrice.toFixed(2)
                                : 'N/D',
                        suggestedMargin: `${suggestedMargin}%`,
                        priceVar: `${isNaN(priceVar) ? '0.0' : priceVar.toFixed(2)}%`,
                        isPromo: prod?.isPromo ? 'Si' : 'No',
                    };
                } catch {
                    return {
                        key: `product-${index}`,
                        name: storeCheckProductName(prod),
                        code: prod?.linkedBarcode?.code || null,
                        storePrice: '0.0',
                        margin: '0.0%',
                        suggestedPrice: 'N/D',
                        suggestedMargin: '0.0%',
                        priceVar: '0.0%',
                        isPromo: 'No',
                    };
                }
            });

            return priceData;
        } catch {
            return [];
        }
    }
}

export default StoreCheckController;
